import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { 
  Megaphone, Search, Plus, Edit, Play, Pause, Loader2, LogOut, Eye,
  LayoutDashboard, Users, Monitor, Calendar, CheckSquare, BarChart3, CreditCard
} from "lucide-react";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { ParkWiseLogo } from "../../../components/ui/parkwise-logo";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const navItems = [
  { path: "/admin/advertising", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/admin/advertising/advertisers", icon: Users, label: "Advertisers" },
  { path: "/admin/advertising/screens", icon: Monitor, label: "Screens & Inventory" },
  { path: "/admin/advertising/campaigns", icon: Megaphone, label: "Campaigns" },
  { path: "/admin/advertising/calendar", icon: Calendar, label: "Bookings Calendar" },
  { path: "/admin/advertising/approvals", icon: CheckSquare, label: "Artwork Approvals" },
  { path: "/admin/advertising/proof-of-play", icon: BarChart3, label: "Proof of Play" },
  { path: "/admin/advertising/billing", icon: CreditCard, label: "Billing & Invoices" },
];

const statusColors = {
  draft: "bg-slate-600",
  pending_approval: "bg-yellow-600",
  approved: "bg-blue-600",
  active: "bg-green-600",
  paused: "bg-orange-600",
  completed: "bg-gray-600",
  cancelled: "bg-red-600",
  expired: "bg-red-800"
};

export default function AdvertisingCampaigns() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  
  // Handle special "pending" param for draft+pending_approval
  const pendingFilter = searchParams.get("pending") === "true";
  const initialStatus = pendingFilter ? "pending" : (searchParams.get("status") || "all");
  const [statusFilter, setStatusFilter] = useState(initialStatus);

  useEffect(() => { checkAuth(); }, []);
  useEffect(() => { if (user) fetchCampaigns(); }, [user, statusFilter]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) { navigate("/admin/login"); }
  };

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter === "pending") {
        // Don't add status param - we'll filter client-side for draft+pending_approval
      } else if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      if (search) params.append("search", search);
      const response = await axios.get(`${API}/advertising/campaigns?${params}`, { withCredentials: true });
      
      // Filter for pending (draft + pending_approval) client-side
      if (statusFilter === "pending") {
        setCampaigns(response.data.filter(c => c.status === "draft" || c.status === "pending_approval"));
      } else {
        setCampaigns(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch campaigns:", error);
    } finally { setLoading(false); }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (e) {}
    navigate("/admin/login");
  };

  if (!user) {
    return (<div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>);
  }

  const filteredCampaigns = campaigns.filter(c => 
    !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.advertiser_name?.toLowerCase().includes(search.toLowerCase())
  );

  // Reconciliation counts
  const countByStatus = {
    draft: campaigns.filter(c => c.status === 'draft' || c.status === 'pending_approval').length,
    active: campaigns.filter(c => c.status === 'active').length,
    paused: campaigns.filter(c => c.status === 'paused').length,
    completed: campaigns.filter(c => c.status === 'completed').length,
  };

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Sidebar */}
      <aside className="w-72 bg-slate-900 border-r border-slate-800 flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <ParkWiseLogo size="md" />
          <p className="text-slate-500 text-xs mt-2">Advertising & Campaigns</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button key={item.path} onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive ? "bg-blue-600/20 text-blue-400" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}>
                <item.icon className="w-5 h-5" /><span className="text-sm font-medium">{item.label}</span>
              </button>
            );
          })}
          <div className="pt-4 border-t border-slate-800 mt-4">
            <button onClick={() => navigate("/admin/dashboard")} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800">
              <LayoutDashboard className="w-5 h-5" /><span className="text-sm">← Back to Main Admin</span>
            </button>
          </div>
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-3 px-2">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full px-2">
            <LogOut className="w-4 h-4" /><span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-white">Campaigns</h1>
              <p className="text-slate-400 mt-1">Manage advertising campaigns</p>
            </div>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-campaign-btn">
              <Plus className="w-4 h-4 mr-2" />New Campaign
            </Button>
          </div>

          {/* Search & Filters */}
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search campaigns..." className="pl-10 w-64 bg-slate-900 border-slate-700 text-white" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 bg-slate-900 border-slate-700 text-white">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Draft & Pending</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending_approval">Pending Approval</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-slate-400">
              <span className="mr-4"><strong className="text-green-400">{countByStatus.active}</strong> active</span>
              <span className="mr-4"><strong className="text-yellow-400">{countByStatus.draft}</strong> draft</span>
              <span><strong className="text-white">{campaigns.length}</strong> total</span>
            </div>
          </div>

          {/* Campaigns Table */}
          <Card className="bg-slate-900 border-slate-800">
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>
            ) : filteredCampaigns.length === 0 ? (
              <CardContent className="py-12 text-center">
                <Megaphone className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No campaigns found. Create your first campaign to get started.</p>
              </CardContent>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Campaign</TableHead>
                    <TableHead className="text-slate-400">Advertiser</TableHead>
                    <TableHead className="text-slate-400">Dates</TableHead>
                    <TableHead className="text-slate-400">Pricing</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Impressions</TableHead>
                    <TableHead className="text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCampaigns.map(campaign => (
                    <TableRow key={campaign.campaign_id} className="border-slate-800 hover:bg-slate-800/50">
                      <TableCell>
                        <div>
                          <p className="text-white font-medium">{campaign.name}</p>
                          <p className="text-xs text-slate-500">{campaign.campaign_id}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-300">{campaign.advertiser_name}</TableCell>
                      <TableCell className="text-slate-400 text-sm">
                        {campaign.start_date?.split('T')[0]} → {campaign.end_date?.split('T')[0]}
                      </TableCell>
                      <TableCell className="text-emerald-400 font-medium">£{(campaign.price_amount || 0).toLocaleString()}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs text-white ${statusColors[campaign.status] || 'bg-slate-600'}`}>
                          {campaign.status?.replace('_', ' ')}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-300">
                        {campaign.actual_impressions?.toLocaleString() || 0} / {campaign.target_impressions?.toLocaleString() || '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white"><Eye className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white"><Edit className="w-4 h-4" /></Button>
                          {campaign.status === 'active' && (
                            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-orange-400"><Pause className="w-4 h-4" /></Button>
                          )}
                          {campaign.status === 'paused' && (
                            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-green-400"><Play className="w-4 h-4" /></Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
